// Keep declarations local when the theme editor loads this asset again.
(() => {
class KamiNavigation extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.animations = new Map();
    const { signal } = this.abort;
    this.dialog = this.querySelector('dialog');
    this.opener = this.querySelector('[data-menu-open]');
    this.menus = [...this.querySelectorAll('[data-desktop-menu]')];
    this.accordions = [...this.querySelectorAll('[data-mobile-category]')];
    this.hover = matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.breakpoint = matchMedia('(min-width: 750px)');
    this.menus.forEach(menu => {
      const summary = menu.querySelector('summary');
      summary.addEventListener('pointerenter', event => {
        if (this.hover.matches && event.pointerType !== 'touch') {
          clearTimeout(this.leaveTimer);
          this.openDesktop(menu);
        }
      }, { signal });
      summary.addEventListener('click', event => {
        event.preventDefault();
        if (menu.open && menu.dataset.closing !== 'true') this.closeDesktop(menu);
        else this.openDesktop(menu);
      }, { signal });
      menu.querySelector('[data-menu-close-desktop]')?.addEventListener('click', () => {
        this.closeDesktop(menu);
        summary.focus();
      }, { signal });
    });
    this.addEventListener('pointerenter', () => clearTimeout(this.leaveTimer), { signal });
    this.addEventListener('pointerleave', event => {
      if (this.hover.matches && event.pointerType !== 'touch') {
        this.leaveTimer = setTimeout(() => this.menus.forEach(menu => this.closeDesktop(menu)), 180);
      }
    }, { signal });
    this.querySelectorAll('.kami-top-link').forEach(link => link.addEventListener('pointerenter', () => {
      if (this.hover.matches) this.menus.forEach(menu => this.closeDesktop(menu));
    }, { signal }));
    document.addEventListener('click', event => {
      if (!this.contains(event.target)) this.menus.forEach(menu => this.closeDesktop(menu));
    }, { signal });
    this.addEventListener('focusout', event => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) this.menus.forEach(menu => this.closeDesktop(menu));
    }, { signal });
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || this.dialog?.open) return;
      const active = this.menus.find(menu => menu.open);
      if (active) { this.closeDesktop(active); active.querySelector('summary').focus(); }
    }, { signal });
    this.opener?.addEventListener('click', () => {
      this.menus.forEach(menu => this.closeDesktop(menu, true));
      this.accordions.forEach((accordion, index) => {
        this.cancelAnimation(accordion);
        accordion.open = index === 0;
      });
      this.dialog.showModal();
      this.dialog.scrollTop = 0;
      this.opener.setAttribute('aria-expanded', 'true');
      this.animate(this.dialog, this.dialog, [{ opacity: 0, transform: 'translateX(24px)' }, { opacity: 1, transform: 'translateX(0)' }], 260);
    }, { signal });
    this.querySelector('[data-menu-close]')?.addEventListener('click', () => this.closeDrawer(), { signal });
    this.dialog?.addEventListener('cancel', event => { event.preventDefault(); this.closeDrawer(); }, { signal });
    this.dialog?.addEventListener('close', () => {
      this.cancelAnimation(this.dialog);
      this.opener?.setAttribute('aria-expanded', 'false');
      if (this.opener?.getClientRects().length) this.opener.focus({ preventScroll: true });
    }, { signal });
    this.dialog?.addEventListener('click', event => { if (event.target === this.dialog) this.closeDrawer(); }, { signal });
    this.accordions.forEach(accordion => {
      accordion.querySelector('summary').addEventListener('click', event => {
        event.preventDefault();
        const opening = !accordion.open || accordion.dataset.closing === 'true';
        if (opening) this.accordions.filter(other => other !== accordion).forEach(other => this.toggleAccordion(other, false));
        this.toggleAccordion(accordion, opening);
      }, { signal });
    });
    this.breakpoint.addEventListener('change', () => {
      clearTimeout(this.leaveTimer);
      this.menus.forEach(menu => this.closeDesktop(menu, true));
      if (this.dialog?.open) { this.cancelAnimation(this.dialog); this.dialog.close(); }
    }, { signal });
  }

  cancelAnimation(key) {
    this.animations.get(key)?.cancel();
    this.animations.delete(key);
    if (key?.dataset) delete key.dataset.closing;
  }

  animate(key, element, frames, duration, complete) {
    this.cancelAnimation(key);
    if (this.reducedMotion.matches || !element.animate) { complete?.(); return; }
    const animation = element.animate(frames, { duration, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'both' });
    this.animations.set(key, animation);
    animation.onfinish = () => {
      if (this.animations.get(key) !== animation) return;
      this.animations.delete(key);
      complete?.();
      animation.cancel();
      delete key.dataset.closing;
    };
  }

  openDesktop(menu) {
    if (menu.open && menu.dataset.closing !== 'true') return;
    clearTimeout(this.leaveTimer);
    this.menus.filter(other => other !== menu).forEach(other => this.closeDesktop(other, true));
    this.cancelAnimation(menu);
    menu.open = true;
    this.classList.add('kami-menu-expanded');
    this.animate(menu, menu.querySelector('.kami-mega-panel'), [{ opacity: 0, transform: 'translateY(-8px)' }, { opacity: 1, transform: 'translateY(0)' }], 240);
  }

  closeDesktop(menu, immediate = false) {
    if (!menu.open || (menu.dataset.closing === 'true' && !immediate)) return;
    const finish = () => {
      menu.open = false;
      this.classList.toggle('kami-menu-expanded', this.menus.some(item => item.open));
    };
    if (immediate) { this.cancelAnimation(menu); finish(); return; }
    const panel = menu.querySelector('.kami-mega-panel');
    const opacity = getComputedStyle(panel).opacity;
    this.animate(menu, panel, [{ opacity, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-6px)' }], 180, finish);
    if (this.animations.has(menu)) menu.dataset.closing = 'true';
  }

  closeDrawer() {
    if (!this.dialog?.open || this.dialog.dataset.closing === 'true') return;
    this.animate(this.dialog, this.dialog, [{ opacity: 1, transform: 'translateX(0)' }, { opacity: 0, transform: 'translateX(24px)' }], 200, () => this.dialog.close());
    if (this.animations.has(this.dialog)) this.dialog.dataset.closing = 'true';
  }

  toggleAccordion(accordion, opening) {
    if (!opening && !accordion.open) return;
    const content = accordion.querySelector(':scope > div');
    this.cancelAnimation(accordion);
    accordion.open = true;
    const height = content.scrollHeight;
    this.animate(accordion, content, opening
      ? [{ height: '0px', opacity: 0 }, { height: `${height}px`, opacity: 1 }]
      : [{ height: `${height}px`, opacity: 1 }, { height: '0px', opacity: 0 }], 200, () => { accordion.open = opening; });
    if (!opening && this.animations.has(accordion)) accordion.dataset.closing = 'true';
  }

  disconnectedCallback() {
    clearTimeout(this.leaveTimer);
    this.animations?.forEach(animation => animation.cancel());
    this.animations?.clear();
    this.abort?.abort();
    if (this.dialog?.open) this.dialog.close();
    this.abort = null;
  }
}
if (!customElements.get('kami-navigation')) customElements.define('kami-navigation', KamiNavigation);

})();
